import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabase';
import { useAuth } from './AuthContext';

const NotificationsContext = createContext({});

// Notification sound URL (subtle notification sound)
const NOTIFICATION_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3';

// Helper to get initial notifications from localStorage
function getInitialNotifications() {
  try {
    return JSON.parse(localStorage.getItem('notifications') || '[]');
  } catch {
    return [];
  }
}

// Helper to get initial sound preference
function getInitialSoundEnabled() {
  return localStorage.getItem('notificationSoundEnabled') === 'true';
}

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  // Use lazy initialization to avoid useEffect setState warnings
  const [notifications, setNotifications] = useState(getInitialNotifications);
  const [unreadCount, setUnreadCount] = useState(() => getInitialNotifications().filter(n => !n.read).length);
  const [soundEnabled, setSoundEnabled] = useState(getInitialSoundEnabled);
  const audioRef = useRef(null);
  const previousStatesRef = useRef({});

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND_URL);
    audioRef.current.volume = 0.5;
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(err => {
        console.log('Could not play notification sound:', err);
      });
    }
  }, [soundEnabled]);

  // Toggle sound
  const toggleSound = useCallback(() => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem('notificationSoundEnabled', String(newValue));
    
    // Play a test sound when enabling
    if (newValue && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(err => {
        console.log('Could not play test sound:', err);
      });
    }
  }, [soundEnabled]);

  // Add a notification
  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      read: false,
      ...notification
    };
    
    setNotifications(prev => {
      const updated = [newNotification, ...prev].slice(0, 50);
      localStorage.setItem('notifications', JSON.stringify(updated));
      return updated;
    });
    setUnreadCount(prev => prev + 1);
    playNotificationSound();
  }, [playNotificationSound]);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev => {
      const updated = prev.map(n => n.id === notificationId ? { ...n, read: true } : n);
      localStorage.setItem('notifications', JSON.stringify(updated));
      return updated;
    });
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => {
      const updated = prev.map(n => ({ ...n, read: true }));
      localStorage.setItem('notifications', JSON.stringify(updated));
      return updated;
    });
    setUnreadCount(0);
  }, []);

  // Clear all notifications
  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
    localStorage.setItem('notifications', '[]');
  }, []);

  // Check for phase completions
  const checkPhaseCompletion = useCallback((tableName, record, ecomId) => {
    const status = record.status?.toLowerCase();

    // Human-in-the-loop: pending user approval for winning product details
    if (tableName === 'winning_product_details' && status === 'pending user approval') {
      const stateKey = `${tableName}-${ecomId}-pending_user_approval`;
      if (previousStatesRef.current[stateKey] !== 'pending_user_approval') {
        previousStatesRef.current[stateKey] = 'pending_user_approval';

        addNotification({
          type: 'human_review',
          title: 'Confirm Product Details',
          message: `Please review and confirm the winning details for product ${ecomId.slice(0, 8)}...`,
          ecomId,
          phase: tableName,
        });
      }
      return;
    }

    if (status === 'completed' || status === 'complete') {
      // Check if we already notified for this
      const stateKey = `${tableName}-${ecomId}`;
      if (previousStatesRef.current[stateKey] !== 'completed') {
        previousStatesRef.current[stateKey] = 'completed';
        
        const phaseNames = {
          'product_reviews': 'Product Research',
          'product_marketing_angles': 'Marketing Angles',
          'ugc_ads': 'UGC Ads',
          'ugc_full_videos': 'UGC Full Videos',
          'ugc_shorts': 'UGC Shorts',
          'winning_product_details': 'Winning Product Details',
          'winning_product_images': 'Winning Product Images',
          'static_ads': 'Static Ads',
          'shopify': 'Shopify Integration',
          'shopify_product_images': 'Shopify Images',
          'shopify_sections': 'Shopify Sections'
        };
        
        const phaseName = phaseNames[tableName] || tableName;
        
        addNotification({
          type: 'phase_complete',
          title: `${phaseName} Completed`,
          message: `Product ${ecomId.slice(0, 8)}... has completed the ${phaseName} phase.`,
          ecomId: ecomId,
          phase: tableName
        });
      }
    }
  }, [addNotification]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!user) return;

    const tables = [
      'product_reviews',
      'product_marketing_angles', 
      'ugc_ads',
      'ugc_full_videos',
      'ugc_shorts',
      'winning_product_details',
      'winning_product_images',
      'static_ads',
      'shopify',
      'shopify_product_images',
      'shopify_sections'
    ];

    const channels = tables.map(table => 
      supabase
        .channel(`${table}-notifications`)
        .on('postgres_changes', 
          { event: 'UPDATE', schema: 'public', table },
          (payload) => {
            if (payload.new && payload.new.ecom_id) {
              checkPhaseCompletion(table, payload.new, payload.new.ecom_id);
            }
          }
        )
        .on('postgres_changes',
          { event: 'INSERT', schema: 'public', table },
          (payload) => {
            if (payload.new && payload.new.ecom_id && payload.new.status) {
              checkPhaseCompletion(table, payload.new, payload.new.ecom_id);
            }
          }
        )
        .subscribe()
    );

    return () => {
      channels.forEach(channel => supabase.removeChannel(channel));
    };
  }, [user, checkPhaseCompletion]);

  const value = {
    notifications,
    unreadCount,
    soundEnabled,
    toggleSound,
    addNotification,
    markAsRead,
    markAllAsRead,
    clearAll
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
}
